import { Injectable, inject } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
} from '@angular/fire/auth';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FSUser } from '../model/firestore-model';
import { DataService } from './data.service';
import { environment } from '../../environments/environment';

const DEV_MOCK_USER: FSUser = {
  uid: 'dev-bypass-uid',
  id: 'dev-bypass-uid',
  name: 'Dev User',
  displayName: 'Dev User',
  email: 'dev@local.dev',
  emailVerified: true,
  isAnonymous: false,
  phoneNumber: null,
  photoURL: '',
  available: true,
  metadata: {
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
  },
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(Auth);
  private data = inject(DataService);

  public user$: BehaviorSubject<FSUser | null | 'loading'> =
    new BehaviorSubject<FSUser | null | 'loading'>('loading');
  userSubs: Subscription | undefined;
  token: string = '';

  constructor() {
    if (environment.bypassAuth) {
      this.user$.next(DEV_MOCK_USER);
      return;
    }

    // console.log('AuthService: Initializing Auth listener. Environment:', environment);

    this.auth.onAuthStateChanged(async (user) => {
      // console.log('AuthService: onAuthStateChanged fired. User:', user ? user.uid : 'null');
      if (user) {
        try {
          this.token = await user.getIdToken();
          // console.log('AuthService: Successfully retrieved getIdToken.');
          
          this.userSubs = this.data.getUser(user.uid).subscribe({
            next: (userDoc) => {
              // console.log('AuthService: getUser subscription emitted:', userDoc ? 'User data found' : 'No user data');
              if (userDoc) {
                const us = <FSUser>userDoc;
                this.user$.next(us);
              } else {
                this.user$.next(null);
              }
            },
            error: (err) => {
              console.error('AuthService: Error fetching user from Firestore:', err);
              // Set to null to exit loading state if firestore fails
              this.user$.next(null);
            }
          });
        } catch (err) {
          console.error('AuthService: Error during user initialization (getIdToken, etc.):', err);
          this.user$.next(null);
        }
      } else {
        // console.log('AuthService: No user detected, setting state to null.');
        try {
          this.userSubs?.unsubscribe();
        } catch (e) {
          console.error('AuthService: Error unsubscribing:', e);
        }
        this.user$.next(null);
      }
    });
  }

  login(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  register(email: string, password: string) {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  logout() {
    return signOut(this.auth);
  }

  loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(this.auth, provider);
  }
}
